import Ajv, { ErrorObject, KeywordDefinition, ValidateFunction } from 'ajv';
import addFormats from 'ajv-formats';
import { get, isEqual } from 'lodash';

import { commonSchema } from './common';
import { marketSurveySchema } from './marketSurvey';
import {
  mixAndMatchSchema,
  optimizationSchema,
  updateProductsSchema,
} from './optimization';
import { ReportKind } from '../model/types';

type Data = Record<string, unknown>;

const FRACTION_EPSILON = 0.001;

interface DataContext {
  instancePath: string;
}

/** Report an error at `field` (dot path, relative to the validated object) when given. */
const fail = (message: string, ctx?: DataContext, field?: string) => [
  field && ctx
    ? {
        message,
        instancePath: `${ctx.instancePath}/${field.replace(/\./g, '/')}`,
      }
    : { message },
];

/**
 * Cross-field rules JSON Schema cannot express on its own. Each is declared in
 * the schema itself so the contract stays readable in one place.
 */
const CUSTOM_KEYWORDS: KeywordDefinition[] = [
  {
    // array of {fraction} objects whose fractions sum to the given value
    keyword: 'fractionsSumTo',
    type: 'array',
    schemaType: 'number',
    errors: true,
    validate: function fractionsSumTo(target: number, data: Data[]) {
      const total = data.reduce((s, r) => s + (Number(r?.fraction) || 0), 0);
      if (Math.abs(total - target) <= FRACTION_EPSILON) return true;
      (fractionsSumTo as { errors?: unknown }).errors = fail(
        `weights must add up to ${target * 100}% (got ${Math.round(total * 1000) / 10}%)`,
      );
      return false;
    },
  },
  {
    // two numeric properties that together make 1
    keyword: 'complementaryFractions',
    type: 'object',
    schemaType: 'array',
    errors: true,
    validate: function complementaryFractions(
      [a, b]: string[],
      data: Data,
      _schema: unknown,
      ctx: DataContext,
    ) {
      const total = Number(data[a]) + Number(data[b]);
      if (Math.abs(total - 1) <= FRACTION_EPSILON) return true;
      (complementaryFractions as { errors?: unknown }).errors = fail(
        `must add up to 1 with ${a}`,
        ctx,
        b,
      );
      return false;
    },
  },
  {
    // object property `a` must not exceed property `b`
    keyword: 'orderedPair',
    type: 'object',
    schemaType: 'array',
    errors: true,
    validate: function orderedPair([a, b]: string[], data: Data) {
      if (Number(data[a]) < Number(data[b])) return true;
      (orderedPair as { errors?: unknown }).errors = fail(
        `${a} must be less than ${b}`,
      );
      return false;
    },
  },
  {
    // parallel arrays where a[i] <= b[i]
    keyword: 'pairwiseOrdered',
    type: 'object',
    schemaType: 'array',
    errors: true,
    validate: function pairwiseOrdered(
      [a, b]: string[],
      data: Data,
      _schema: unknown,
      ctx: DataContext,
    ) {
      const left = (get(data, a) as number[]) ?? [];
      const right = (get(data, b) as number[]) ?? [];
      const bad = left.findIndex((v, i) => v > right[i]);
      if (bad === -1) return true;
      (pairwiseOrdered as { errors?: unknown }).errors = fail(
        `must not exceed ${b}[${bad}]`,
        ctx,
        `${a}.${bad}`,
      );
      return false;
    },
  },
  {
    // groups of (possibly nested, dot-separated) array paths with equal lengths
    keyword: 'equalLengths',
    type: 'object',
    schemaType: 'array',
    errors: true,
    validate: function equalLengths(
      groups: string[][],
      data: Data,
      _schema: unknown,
      ctx: DataContext,
    ) {
      for (const group of groups) {
        const lengths = group.map(
          (p) => ((get(data, p) as unknown[]) ?? []).length,
        );
        if (new Set(lengths).size > 1) {
          (equalLengths as { errors?: unknown }).errors = fail(
            `must have the same length as ${group[0]}`,
            ctx,
            group[lengths.findIndex((l) => l !== lengths[0])],
          );
          return false;
        }
      }
      return true;
    },
  },
  {
    // every item of the first array also appears in the second
    keyword: 'subsetOf',
    type: 'object',
    schemaType: 'array',
    errors: true,
    validate: function subsetOf(
      pairs: string[][],
      data: Data,
      _schema: unknown,
      ctx: DataContext,
    ) {
      for (const [sub, sup] of pairs) {
        const superset = new Set((get(data, sup) as unknown[]) ?? []);
        const missing = ((get(data, sub) as unknown[]) ?? []).filter(
          (v) => !superset.has(v),
        );
        if (missing.length > 0) {
          (subsetOf as { errors?: unknown }).errors = fail(
            `must be within ${sup} (not found: ${missing.join(', ')})`,
            ctx,
            sub,
          );
          return false;
        }
      }
      return true;
    },
  },
  {
    // arrays that must not share any value
    keyword: 'disjoint',
    type: 'object',
    schemaType: 'array',
    errors: true,
    validate: function disjoint(
      paths: string[],
      data: Data,
      _schema: unknown,
      ctx: DataContext,
    ) {
      const seen = new Map<unknown, string>();
      for (const path of paths) {
        for (const v of (get(data, path) as unknown[]) ?? []) {
          if (seen.has(v) && seen.get(v) !== path) {
            (disjoint as { errors?: unknown }).errors = fail(
              `"${String(v)}" is already in ${seen.get(v)}`,
              ctx,
              path,
            );
            return false;
          }
          seen.set(v, path);
        }
      }
      return true;
    },
  },
  {
    // at least one of the boolean properties is true
    keyword: 'anyTrue',
    type: 'object',
    schemaType: 'array',
    errors: true,
    validate: function anyTrue(props: string[], data: Data) {
      if (props.some((p) => data[p] === true)) return true;
      (anyTrue as { errors?: unknown }).errors = fail(
        `at least one of ${props.join(', ')} must be true`,
      );
      return false;
    },
  },
  {
    // at most one of the boolean properties is true
    keyword: 'atMostOneTrue',
    type: 'object',
    schemaType: 'array',
    errors: true,
    validate: function atMostOneTrue(props: string[], data: Data) {
      if (props.filter((p) => data[p] === true).length <= 1) return true;
      (atMostOneTrue as { errors?: unknown }).errors = fail(
        `only one of ${props.join(', ')} can be true`,
      );
      return false;
    },
  },
  {
    // array of objects unique by a property
    keyword: 'uniqueItemsBy',
    type: 'array',
    schemaType: 'string',
    errors: true,
    validate: function uniqueItemsBy(prop: string, data: Data[]) {
      const values = data.map((d) => d?.[prop]);
      if (new Set(values).size === values.length) return true;
      (uniqueItemsBy as { errors?: unknown }).errors = fail(
        `items must have a unique ${prop}`,
      );
      return false;
    },
  },
];

export const SCHEMAS_BY_KIND = {
  market_survey: marketSurveySchema,
  optimization: optimizationSchema,
  mix_and_match: mixAndMatchSchema,
  update_products: updateProductsSchema,
} as const;

let ajv: Ajv | null = null;
const compiled = new Map<ReportKind, ValidateFunction>();

const getAjv = (): Ajv => {
  if (ajv) return ajv;
  ajv = new Ajv({ allErrors: true, strict: true, allowUnionTypes: true });
  addFormats(ajv, ['uuid']);
  CUSTOM_KEYWORDS.forEach((k) => ajv.addKeyword(k));
  ajv.addSchema(commonSchema);
  return ajv;
};

export const getValidator = (kind: ReportKind): ValidateFunction => {
  const cached = compiled.get(kind);
  if (cached) return cached;
  const validate = getAjv().compile(SCHEMAS_BY_KIND[kind]);
  compiled.set(kind, validate);
  return validate;
};

export interface SchemaIssue {
  /** e.g. `age_ranges[0].min` or `(root)` */
  path: string;
  message: string;
}

const toPath = (instancePath: string): string =>
  instancePath
    .split('/')
    .filter(Boolean)
    .map((seg, i) =>
      /^\d+$/.test(seg) ? `[${seg}]` : i === 0 ? seg : `.${seg}`,
    )
    .join('') || '(root)';

const describe = (error: ErrorObject): SchemaIssue => {
  const path = toPath(error.instancePath);
  const params = error.params as Record<string, unknown>;
  switch (error.keyword) {
    case 'required':
      return {
        path: toPath(`${error.instancePath}/${params.missingProperty}`),
        message: 'is required',
      };
    case 'additionalProperties':
      return {
        path: toPath(`${error.instancePath}/${params.additionalProperty}`),
        message: 'is not part of the contract',
      };
    case 'enum':
      return {
        path,
        message: `must be one of: ${(params.allowedValues as unknown[]).join(', ')}`,
      };
    case 'const':
      return {
        path,
        message: `must be ${JSON.stringify(params.allowedValue)}`,
      };
    case 'format':
      return { path, message: `must be a valid ${params.format}` };
    case 'pattern':
      return { path, message: 'has an invalid format' };
    default:
      return { path, message: error.message ?? 'is invalid' };
  }
};

/** Composite keywords whose children already explain the failure. */
const NOISE = new Set(['if', 'anyOf', 'oneOf', 'allOf']);

export const formatSchemaErrors = (
  errors: ErrorObject[] | null | undefined,
): SchemaIssue[] => {
  const issues = (errors ?? [])
    .filter((e) => !NOISE.has(e.keyword))
    .map(describe);
  return issues.filter(
    (issue, i) => issues.findIndex((other) => isEqual(other, issue)) === i,
  );
};

export interface SchemaValidationResult {
  valid: boolean;
  issues: SchemaIssue[];
}

export const validateReportParams = (
  kind: ReportKind,
  params: unknown,
): SchemaValidationResult => {
  const validate = getValidator(kind);
  // Validate a JSON round-trip so `undefined` behaves exactly as on the wire.
  const valid = validate(JSON.parse(JSON.stringify(params ?? null))) as boolean;
  return { valid, issues: valid ? [] : formatSchemaErrors(validate.errors) };
};
